import React, { useState, useEffect, useRef } from 'react';
import _isEqual from 'lodash/isEqual';
import {
  Select,
  Button,
  List,
  Space,
  Avatar
} from 'antd';
import styles from './fileSharePanel.module.scss';
import KoIcon from '@kwapio/icons';
import { KoAvatar, KoButton,  KoModal, KoSelect, KoText, KoTitle } from 'packages';
import { FilesAPI } from './files.service';

const { Option } = Select;

const permissionOptions = [
  { label: 'Viewer', value: 0 },
  { label: 'Editor', value: 1 },
  { label: 'Commenter', value: 2 }
];

const FileSharePanel = ({ initialShared = { users: [], groups: [] }, onSave, title, className, onClose }) => {
  const [sharedUsers, setSharedUsers] = useState(initialShared.users || []);
  const [sharedGroups, setSharedGroups] = useState(initialShared.groups || []);
  const hasMounted = useRef(false);
  const lastSavedRef = useRef({ users: sharedUsers, groups: sharedGroups });

  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [selectedPermission, setSelectedPermission] = useState(0);
  const [users, setUsers] = useState([]);
  const [groups, setGroups] = useState([]);


  const getUser = (id) => users.find((u) => u.userId === id);
  const getGroup = (id) => groups.find((g) => g.groupId === id);

  useEffect(() => {
    let isMounted = true;

    const fetchData = async() => {
      try {
        const [userData, groupData] = await Promise.all([
          FilesAPI.getUsers(),
          FilesAPI.getGroups()
        ]);
        if (isMounted) {
          setUsers(userData['users']);
          setGroups(groupData['groups']);
        }
      } catch (error) {
        console.error("Error fetching users or groups:", error);
      }
    };

    fetchData();

    return () => {
      isMounted = false;
    };
  }, []);


  useEffect(() => {
    if (hasMounted.current) {
      const current = { users: sharedUsers, groups: sharedGroups };
      if (!_isEqual(current, lastSavedRef.current)) {
        onSave?.(current);
        lastSavedRef.current = current;
      }
    } else {
      hasMounted.current = true;
    }
  }, [sharedUsers, sharedGroups]);

  const handleAddUser = () => {
    if (!selectedUser || sharedUsers.find((u) => u.userId === selectedUser)) return;
    setSharedUsers([...sharedUsers, { userId: selectedUser, permission: selectedPermission }]);
    setSelectedUser(null);
  };

  const handleAddGroup = () => {
    if (!selectedGroup || sharedGroups.find((g) => g.groupId === selectedGroup)) return;
    setSharedGroups([...sharedGroups, { groupId: selectedGroup, permission: selectedPermission }]);
    setSelectedGroup(null);
  };

  const renderAvatar = (name, avatarUrl, fallback) =>
    avatarUrl ? (
      <Avatar src={avatarUrl} />
    ) : (
      <Avatar className={name?.charAt(0).toLowerCase() || 'a'}>{(name || fallback || 'U')[0].toUpperCase()}</Avatar>
    );

  const mergedShared = [
    ...(sharedUsers || []).map((u) => ({ ...u, type: 'user' })),
    ...(sharedGroups || []).map((g) => ({ ...g, type: 'group' }))
  ];
  return (
    <KoModal
      title={`Share ${title ? title : ''}`}
      className={className}
      visible={true}
      centered
      width={'700px'}
      onClose={() => onClose(false)}
      footer={[
        // <KoButton key="cancel" onClick={() => onClose(false)} label="Copy Link" />,
        <KoButton key="submit" type="primary" onClick={() => onClose(false)} label="Done" />]}
    >
      <div className={`${styles.wrapper}`}>
        <div className={`${styles.selection} ${className ? className : ''}`}>
          <KoSelect
            value={selectedUser || selectedGroup}
            onChange={(value, option) => {
              if (option?.entityType === 'user') {
                setSelectedUser(value);
                setSelectedGroup(null);
              } else if (option?.entityType === 'group') {
                setSelectedGroup(value);
                setSelectedUser(null);
              }
            }}
            showSearch
            allowClear
            mode={''}
            placeholder="Select People or Groups"
            filterOption={(input, option) =>
              (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
            }
            optionRender={(option) => {
              console.log("OPTION:", option);
              return (
                <div className="userDpWithName">
                  <KoAvatar
                    inLine="0rem"
                    size={30}
                    options={[{ name: option.data.label, logo: option.data.emoji }]}
                  />
                  <div className="userDpWithNameEmail">
                    <KoText className="name" text={option.data.label} />
                    <KoText className="email" text={option.data.data.desc} />
                  </div>
                </div>
              );
            }}
            options={[
              ...(users || []).map((user) => {
                const label = `${user.profile?.firstName || user.email} ${user.profile?.lastName || ''}`;
                const userId = user?.userId || `user-${user?.email || Math.random()}`;
                return {
                  key: `user-${userId}`,
                  value: userId,
                  label,
                  data: {
                    label,
                    desc: user.email || '',
                    emoji: user.profile?.logo?.[0]?.url || ''
                  },
                  entityType: 'user'
                };
              }),

              ...(groups || []).map((group) => {
                const groupId = group?.groupId || `group-${group?.name || Math.random()}`;
                return {
                  key: `group-${groupId}`,
                  value: groupId,
                  label: group.name,
                  data: {
                    label: group.name,
                    desc: group.createdByEmail || '',
                    emoji: ''
                  },
                  entityType: 'group'
                };
              })
            ]}

          />


          <Select value={selectedPermission} style={{ width: 120 }} onChange={setSelectedPermission}>
            {permissionOptions.map((opt) => (
              <Option key={opt.value} value={opt.value}>{opt.label}</Option>
            ))}
          </Select>

          <Button
            type="primary"
            onClick={() => {
              if (selectedUser) handleAddUser();
              else if (selectedGroup) handleAddGroup();
            }}
          >
            Send
          </Button>
        </div>

        <List
          bordered
          className={`${styles.list} ${className ? className : ''}`}
          dataSource={mergedShared}
          header={<KoTitle level={4} text="People & Groups with access" />}
          renderItem={(item) => {
            const isUser = item.type === 'user';
            const entity = isUser ? getUser(item.userId) : getGroup(item.groupId);
            const name = isUser
              ? `${entity?.profile?.firstName || ''} ${entity?.profile?.lastName || ''}`.trim()
              : entity?.name || item.groupId;
            const email = isUser ? entity?.email : entity?.createdByEmail;
            const avatarUrl = isUser ? entity?.profile?.logo?.[0]?.url : '';
            const key = isUser ? item.userId : item.groupId;

            return (
              <List.Item
                key={key}
                actions={[
                  <Select
                    key={`perm-${key}`}
                    value={item.permission}
                    style={{ width: 120 }}
                    variant="filled"
                    onChange={(value) => {
                      if (isUser) {
                        setSharedUsers(sharedUsers.map((u) =>
                          u.userId === item.userId ? { ...u, permission: value } : u
                        ));
                      } else {
                        setSharedGroups(sharedGroups.map((g) =>
                          g.groupId === item.groupId ? { ...g, permission: value } : g
                        ));
                      }
                    }}
                  >
                    {permissionOptions.map((opt) => (
                      <Option key={opt.value} value={opt.value}>{opt.label}</Option>
                    ))}
                  </Select>,
                  <Button
                    key={`del-${key}`}
                    type="default"
                    icon={<KoIcon width={16} height={16} name={'DeleteOutlined'} />}
                    onClick={() => {
                      if (isUser) {
                        setSharedUsers(sharedUsers.filter((u) => u.userId !== item.userId));
                      } else {
                        setSharedGroups(sharedGroups.filter((g) => g.groupId !== item.groupId));
                      }
                    }}
                  />
                ]}
              >
                <Space direction="vertical" size={0}>
                  <Space>
                    {renderAvatar(name, avatarUrl, email)}
                    <div>
                      <div>{name}</div>
                      <small style={{ color: 'gray' }}>{email}</small>
                    </div>
                  </Space>
                </Space>
              </List.Item>
            );
          }}
        />


      </div>
    </KoModal>
  );
};

export default FileSharePanel;
